import React, { FunctionComponent } from 'react'
import styled, { css } from 'styled-components'
import type { Legends, OutcomeTypeColors } from '@hooks/useSportsStandings'

type StandingsLegendProps = {
  legend: Legends
  outcomeTypeColors: OutcomeTypeColors
  hasInvertedColors: boolean
}

const StyledLegendWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing16, spacing4 },
    },
  }) => css`
    display: flex;
    flex-wrap: wrap;
    margin-top: ${spacing16};
    gap: ${spacing4} ${spacing16};
  `}
`
const StyledLegendItemWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing8 },
    },
  }) => css`
    display: flex;
    gap: 0 ${spacing8};
    align-items: center;
  `}
`
const StyledLegendColorIndicator = styled.div`
  ${({
    theme: {
      spacing: { spacing12 },
    },
    color,
  }) => css`
    width: ${spacing12};
    height: ${spacing12};
    background-color: ${color};
    border-radius: 50%;
  `}
`
const StyledLegendLabel = styled.div<
  Pick<StandingsLegendProps, 'hasInvertedColors'>
>`
  ${({
    theme: {
      typography: {
        eyebrows: {
          small1: { bundledCSS: small1EyebrowsCSS },
        },
      },
      color: {
        tertiary: { grey100: grey100Color, grey1000: grey1000Color },
      },
    },
    hasInvertedColors,
  }) => css`
    ${small1EyebrowsCSS};
    color: ${hasInvertedColors ? grey100Color : grey1000Color};
  `}
`

const StandingsLegend: FunctionComponent<StandingsLegendProps> = ({
  legend,
  outcomeTypeColors,
  hasInvertedColors,
}) => {
  const renderLegendItems = (legend: Legends) =>
    legend.map((item) => {
      const key = Object.keys(item)[0] as keyof OutcomeTypeColors
      const value = item[key]
      return (
        <StyledLegendItemWrapper key={key}>
          <StyledLegendColorIndicator color={outcomeTypeColors[key]} />
          <StyledLegendLabel hasInvertedColors={hasInvertedColors}>
            {value}
          </StyledLegendLabel>
        </StyledLegendItemWrapper>
      )
    })

  return <StyledLegendWrapper>{renderLegendItems(legend)}</StyledLegendWrapper>
}

export default StandingsLegend
